"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatDate = formatDate;
exports.isOverdue = isOverdue;
exports.LetterContext = LetterContext;
exports.LetterMetaStrip = LetterMetaStrip;
exports.LetterRowMarkers = LetterRowMarkers;
exports.LetterRowContent = LetterRowContent;
/**
 * Wspólna zawartość wiersza rejestru pism (PS.APP.01, pack PIS, checkpoint PIS-2).
 *
 * Układ zatwierdzony przez właściciela na makiecie `makieta-rejestr-pism-v1.html`,
 * zakładka „Hybryda” z poprawką „kamień milowy raz, gdy wspólny”. Decyzje i ich powody:
 * `20_projects/Aplikacje/PS.APP.01/decisions/2026-07-31-pis-uklad-rejestru-pism.md`.
 *
 * Komponent mieszka w module pism, a moduł ofert go importuje (nie odwrotnie) — stąd dwa
 * konteksty: kontraktowy (`contract`) i ofertowy (`offer`).
 */
const free_solid_svg_icons_1 = require("@fortawesome/free-solid-svg-icons");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const react_1 = __importDefault(require("react"));
const MainSetupReact_1 = __importDefault(require("../../React/MainSetupReact"));
const Tools_1 = __importDefault(require("../../React/Tools/Tools"));
const ToolsDate_1 = __importDefault(require("../../React/Tools/ToolsDate"));
const LetterSignedBadge_1 = require("../Signing/LetterSignedBadge");
/** Paleta z zatwierdzonej makiety. Kolor agenta (`bot`) stoi POZA paletą statusów pisma —
 *  znacznik mówi o pochodzeniu wpisu, nie o stanie pisma. */
const C = {
    ink: "#16211c",
    inkSoft: "#3c4c44",
    muted: "#6b7c73",
    faint: "#94a49b",
    line: "#c2d1c9",
    accent: "#166243",
    chipOtherBg: "#edf1ee",
    warn: "#8f5406",
    bot: "#4b5a90",
};
const S = {
    ctxTop: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        gap: "7px",
        fontSize: ".8rem",
    },
    chipBase: {
        fontWeight: 700,
        borderRadius: "4px",
        padding: "1px 7px",
        fontSize: ".74rem",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
    },
    who: { color: C.inkSoft, fontWeight: 600 },
    ctype: { color: C.faint },
    proj: { color: C.muted, fontSize: ".76rem", fontVariantNumeric: "tabular-nums" },
    under: { color: C.muted, fontSize: ".76rem" },
    underStrong: { color: C.inkSoft, fontWeight: 600 },
    caseRow: {
        fontSize: ".8rem",
        color: C.inkSoft,
        display: "grid",
        gridTemplateColumns: "15px 1fr",
    },
    tree: { color: C.line, fontFamily: 'ui-monospace, "Cascadia Mono", Consolas, monospace' },
    caseNumber: { fontWeight: 700, fontVariantNumeric: "tabular-nums", color: C.ink },
    caseType: { color: C.muted },
    milestone: { color: C.faint },
    separator: { color: C.line },
    msHead: { fontSize: ".8rem", color: C.faint, paddingLeft: "15px" },
    number: { fontSize: "1.06rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
    subject: { color: C.inkSoft },
    label: { color: C.faint },
    meta: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "4px 16px",
        fontSize: ".76rem",
        color: C.muted,
    },
    metaItem: { display: "inline-flex", alignItems: "center", gap: "5px" },
    metaKey: {
        textTransform: "uppercase",
        letterSpacing: ".08em",
        fontSize: ".66rem",
        color: C.faint,
        fontWeight: 700,
    },
    metaValue: { color: C.inkSoft, fontVariantNumeric: "tabular-nums" },
    metaValueOverdue: { color: C.warn, fontWeight: 600, fontVariantNumeric: "tabular-nums" },
    event: { fontSize: ".76rem", color: C.muted },
    eventType: { color: C.inkSoft, fontWeight: 600 },
    bot: { color: C.bot },
};
/** „2026-07-30” albo ISO -> „30-07-2026”. Nie rzuca na pustej ani niespodziewanej wartości —
 *  wiersz rejestru nie ma prawa wywalić listy przez złą datę.
 *  Eksportowana, bo rejestr kontraktów (Contracts/ContractsList/ContractRowContent.tsx) formatuje
 *  daty identycznie — kopia rozjechałaby oba rejestry przy pierwszej poprawce formatu. */
function formatDate(value) {
    if (!value)
        return "—";
    const parts = value.slice(0, 10).split("-");
    if (parts.length !== 3 || parts[0].length !== 4)
        return String(value);
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
}
/** Termin minął (data wcześniejsza niż dzisiaj). Eksportowana z tego samego powodu co formatDate. */
function isOverdue(dueDate) {
    const due = new Date(dueDate.slice(0, 10));
    if (Number.isNaN(due.getTime()))
        return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
}
function isOurContract(contract) {
    return !!contract.ourId;
}
function entitiesLabel(entities) {
    if (!entities?.length)
        return "";
    return entities.map((entity) => entity.shortName || entity.name || "").filter(Boolean).join(" · ");
}
/** Nazwa kamienia milowego. Kamienie unikalne w kontrakcie mają puste `name`, więc niosą je
 *  wyłącznie typy; śmieciowe nazwy jednoznakowe („.”, „-”) pomijamy. */
function milestoneLabel(milestone) {
    if (!milestone)
        return "";
    const typeName = milestone._type?.name?.trim() || "";
    const ownName = milestone.name?.trim() || "";
    if (ownName.length > 1)
        return typeName ? `${typeName} - ${ownName}` : ownName;
    return typeName;
}
function groupCasesByContext(cases, kind) {
    const groups = [];
    for (const singleCase of cases || []) {
        const parent = singleCase._parent;
        const contract = kind === "contract" ? parent?._contract : undefined;
        const offer = kind === "offer" ? parent?._offer : undefined;
        const key = `${contract?.id ?? ""}|${offer?.id ?? ""}`;
        const existing = groups.find((group) => group.key === key);
        if (existing)
            existing.cases.push(singleCase);
        else
            groups.push({ key, contract, offer, cases: [singleCase] });
    }
    return groups;
}
function ContractHeader({ contract, projectOurId }) {
    const ours = isOurContract(contract);
    const chipLabel = ours ? contract.ourId : contract.number || contract._ourIdOrNumber_Alias || "—";
    const chipStyle = ours
        ? { ...S.chipBase, color: "#fff", backgroundColor: C.accent }
        : { ...S.chipBase, color: C.ink, backgroundColor: C.chipOtherBg, border: `1px solid ${C.line}` };
    const contractors = entitiesLabel(contract._contractors);
    const relatedOurId = ours ? "" : contract.ourIdRelated || "";
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("div", { style: S.ctxTop },
            react_1.default.createElement("span", { style: chipStyle, title: ours ? "Nasz kontrakt" : "Kontrakt wykonawcy" }, chipLabel),
            projectOurId && react_1.default.createElement("span", { style: S.proj }, projectOurId),
            react_1.default.createElement("span", { style: S.who }, ours ? "nasz kontrakt" : contractors),
            contract._type?.name && react_1.default.createElement("span", { style: S.ctype }, contract._type.name)),
        relatedOurId && (react_1.default.createElement("div", { style: S.under },
            "nasz nadz\u00F3r: ",
            react_1.default.createElement("b", { style: S.underStrong }, relatedOurId)))));
}
function OfferHeader({ offer }) {
    const chipStyle = {
        ...S.chipBase,
        color: C.ink,
        backgroundColor: C.chipOtherBg,
        border: `1px solid ${C.line}`,
    };
    return (react_1.default.createElement("div", { style: S.ctxTop },
        react_1.default.createElement("span", { style: chipStyle, title: "Oferta" }, offer.alias || "—"),
        offer.employerName && react_1.default.createElement("span", { style: S.who }, offer.employerName),
        offer._type?.name && react_1.default.createElement("span", { style: S.ctype }, offer._type.name)));
}
function CasesTree({ cases }) {
    const labels = cases.map((singleCase) => milestoneLabel(singleCase._parent));
    // Kamień milowy raz nad drzewkiem, gdy wspólny dla wszystkich spraw; przy każdej, gdy różny.
    const sharedMilestone = labels.every((label) => label === labels[0]) ? labels[0] : "";
    return (react_1.default.createElement(react_1.default.Fragment, null,
        sharedMilestone && react_1.default.createElement("div", { style: S.msHead }, sharedMilestone),
        cases.map((singleCase, index) => (react_1.default.createElement("div", { key: singleCase.id, style: S.caseRow },
            react_1.default.createElement("span", { style: S.tree }, index === cases.length - 1 ? "└" : "├"),
            react_1.default.createElement("span", null,
                !sharedMilestone && labels[index] && (react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement("span", { style: S.milestone }, labels[index]),
                    " ",
                    react_1.default.createElement("span", { style: S.separator }, "\u203A"),
                    " ")),
                react_1.default.createElement("span", { style: S.caseNumber }, singleCase._displayNumber),
                " ",
                singleCase._type?.name && react_1.default.createElement("span", { style: S.caseType }, singleCase._type.name),
                singleCase.name?.trim() ? ` — ${singleCase.name.trim()}` : ""))))));
}
/** Nagłówek kontekstu wiersza: gdzie to pismo wisi. */
function LetterContext({ letter, context }) {
    const groups = groupCasesByContext(letter._cases, context);
    if (!groups.length)
        return null;
    const projectOurId = context === "contract" ? letter._project?.ourId : undefined;
    return (react_1.default.createElement("div", { className: "d-flex flex-column", style: { gap: "3px" } }, groups.map((group) => (react_1.default.createElement(react_1.default.Fragment, { key: group.key },
        group.contract && react_1.default.createElement(ContractHeader, { contract: group.contract, projectOurId: projectOurId }),
        group.offer && react_1.default.createElement(OfferHeader, { offer: group.offer }),
        react_1.default.createElement(CasesTree, { cases: group.cases }))))));
}
/** Pasek meta: daty schodzą tu z kolumn. Etykieta drugiej daty zależy od kierunku pisma. */
function LetterMetaStrip({ letter }) {
    const dueDate = letter.responseDueDate;
    return (react_1.default.createElement("div", { style: S.meta },
        react_1.default.createElement("span", { style: S.metaItem },
            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faCalendarPlus }),
            react_1.default.createElement("span", { style: S.metaKey }, "utworzono"),
            react_1.default.createElement("span", { style: S.metaValue }, formatDate(letter.creationDate))),
        react_1.default.createElement("span", { style: S.metaItem },
            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: letter.isOur ? free_solid_svg_icons_1.faPaperPlane : free_solid_svg_icons_1.faInbox }),
            react_1.default.createElement("span", { style: S.metaKey }, letter.isOur ? "wysłano" : "wpłynęło"),
            react_1.default.createElement("span", { style: S.metaValue }, formatDate(letter.registrationDate))),
        dueDate && (react_1.default.createElement("span", { style: S.metaItem },
            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faClock }),
            react_1.default.createElement("span", { style: S.metaKey }, "odpowied\u017A do"),
            react_1.default.createElement("span", { style: isOverdue(dueDate) ? S.metaValueOverdue : S.metaValue }, formatDate(dueDate))))));
}
function LetterLastEvent({ letter }) {
    const event = letter._lastEvent;
    if (!event)
        return null;
    // Znacznik agenta przy autorze pokazujemy tylko przy zdarzeniu utworzenia — po zatwierdzeniu
    // autorstwo przechodzi na człowieka i to jego nazwisko stoi w linii ostatniego zdarzenia.
    const showAgentMark = !!letter._isCreatedByAgent && event.eventType === "CREATED";
    return (react_1.default.createElement("div", { style: S.event },
        react_1.default.createElement("span", { style: S.eventType }, Tools_1.default.getLabelFromKey(event.eventType, MainSetupReact_1.default.LetterEventType)),
        " ",
        ToolsDate_1.default.dateToDDmmmYYYYHHMM(event._lastUpdated),
        " \u00B7",
        " ",
        showAgentMark && (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement("span", { title: "Wpis za\u0142o\u017Cony przez agenta", style: S.bot },
                react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faRobot })),
            " ")),
        event._editor?.name,
        " ",
        event._editor?.surname));
}
/** Kolumna znaczników: kierunek pisma, dokumentacja zatwierdzona, znacznik agenta. */
function LetterRowMarkers({ letter }) {
    return (react_1.default.createElement("div", { className: "d-flex flex-column align-items-center gap-2" },
        react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: letter.isOur ? free_solid_svg_icons_1.faPaperPlane : free_solid_svg_icons_1.faEnvelope, size: "lg" }),
        letter.addedToApprovedDocumentation && (
        // `title` na FontAwesomeIcon nie trafia do DOM (idzie do <title> w SVG), więc
        // podpowiedź musi wisieć na opakowaniu — tak samo jak przy znaczniku agenta.
        react_1.default.createElement("span", { title: "Dokumentacja zatwierdzona", className: "text-success" },
            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faClipboardCheck, size: "lg" }))),
        letter._isCreatedByAgent && (react_1.default.createElement("span", { title: "Zarejestrowane przez agenta", style: S.bot },
            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faRobot, size: "lg" })))));
}
function LetterRowContent({ letter, context, renderStatus }) {
    return (react_1.default.createElement("div", { className: "d-flex flex-column", style: { gap: "9px", wordBreak: "break-word" } },
        react_1.default.createElement(LetterContext, { letter: letter, context: context }),
        react_1.default.createElement("div", { className: "d-flex flex-wrap align-items-baseline", style: { gap: "9px" } },
            letter.number !== undefined && letter.number !== null && letter.number !== "" && (react_1.default.createElement("span", { style: S.number }, letter.number)),
            renderStatus?.(letter),
            letter.isOur && react_1.default.createElement(LetterSignedBadge_1.LetterSignedBadge, { letterId: letter.id })),
        react_1.default.createElement("div", { style: { ...S.subject, whiteSpace: "pre-line" } },
            react_1.default.createElement("span", { style: S.label }, "Dotyczy:"),
            " ",
            letter.description,
            letter.relatedLetterNumber && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement("br", null),
                react_1.default.createElement("span", { style: S.label }, "W odpowiedzi na pismo nr:"),
                " ",
                letter.relatedLetterNumber)),
            letter.responseIKNumber && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement("br", null),
                react_1.default.createElement("span", { style: S.label }, "Odpowied\u017A IK:"),
                " ",
                letter.responseIKNumber))),
        react_1.default.createElement(LetterMetaStrip, { letter: letter }),
        react_1.default.createElement(LetterLastEvent, { letter: letter })));
}
